import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// Marketing/public pages that should use light theme by default (regardless of auth status)
const LIGHT_THEME_ROUTES = [
  '/auth/',
  '/activation',
  '/verify-email',
  '/mobile-login',
  '/pricing',
  '/rates',
  '/faq',
  '/contact-sales',
  '/help',
  '/support',
  '/api-docs',
  '/developers',
  '/select-plan',
  '/kyc-address',
  '/checkout',
  '/demo/',
  '/plans',
  '/reactivate',
  '/get-number',
];

function isMarketingRoute(path: string): boolean {
  return LIGHT_THEME_ROUTES.some(route => {
    // Entries already ending in `/` are prefixes by intent (e.g. `/auth/`).
    if (route.endsWith('/')) return path.startsWith(route);
    // Otherwise match a whole segment, so `/help` does not also claim
    // `/helpdesk` — which is an authenticated page and must stay dark.
    return path === route || path.startsWith(route + '/');
  });
}

function getStoredTheme(): Theme | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem('theme');
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return null;
}

function getCurrentPath(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname;
}

interface ThemeProviderProps {
  children: ReactNode;
  isAuthenticated?: boolean;
}

export function ThemeProvider({ children, isAuthenticated = false }: ThemeProviderProps) {
  // Get current path directly from window to avoid Router context dependency
  const [currentPath, setCurrentPath] = useState(getCurrentPath);
  
  // Determine default theme based on route and auth status
  const getDefaultTheme = (): Theme => {
    // If user has explicitly set a theme, respect it
    const stored = getStoredTheme();
    if (stored) return stored;
    
    // Marketing pages always use light theme (regardless of auth status)
    if (isMarketingRoute(currentPath)) {
      return 'light';
    }
    
    // Root path is the public landing page only before authentication.
    if (currentPath === '/' && !isAuthenticated) {
      return 'light';
    }
    
    // Light by default everywhere; dark stays available via the theme toggle
    return 'light';
  };

  const [theme, setThemeState] = useState<Theme>(getDefaultTheme);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(
    theme === 'system' ? getSystemTheme() : theme
  );

  const applyTheme = (newTheme: Theme) => {
    const root = document.documentElement;
    const resolved = newTheme === 'system' ? getSystemTheme() : newTheme;
    
    root.classList.remove('light', 'dark');
    root.classList.add(resolved);
    setResolvedTheme(resolved);
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
    applyTheme(newTheme);
  };

  const toggleTheme = () => {
    const newTheme = resolvedTheme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  // Listen for route changes via popstate and custom events
  useEffect(() => {
    const handleRouteChange = () => {
      setCurrentPath(getCurrentPath());
    };

    // Listen to browser back/forward
    window.addEventListener('popstate', handleRouteChange);
    
    // Use MutationObserver to detect SPA route changes
    const observer = new MutationObserver(() => {
      const newPath = getCurrentPath();
      if (newPath !== currentPath) {
        setCurrentPath(newPath);
      }
    });
    
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      observer.disconnect();
    };
  }, [currentPath]);

  // Apply theme on mount
  useEffect(() => {
    applyTheme(theme);
  }, []);

  // Update theme when auth status or route changes (only if user hasn't set a preference)
  useEffect(() => {
    const stored = getStoredTheme();
    if (!stored) {
      const newDefault = getDefaultTheme();
      if (newDefault !== theme) {
        setThemeState(newDefault);
        applyTheme(newDefault);
      }
    }
  }, [isAuthenticated, currentPath]);

  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => applyTheme('system');
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
